import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import AnimatedLogo from "@/components/AnimatedLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TRUEUP_FREQUENCIES, TIMEZONES, DURATION_UNITS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, FolderKanban, ArrowUpRight, Loader2, Briefcase, LayoutDashboard, Archive, UserCircle2 } from "lucide-react";

const FOLDER_COLORS = ["#0044FF", "#10B981", "#7C3AED", "#F59E0B", "#EC4899", "#0EA5E9", "#EF4444", "#14B8A6"];

const EMPTY_IMG = "https://images.unsplash.com/photo-1531973576160-7125cd663d86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjB3b3JraW5nJTIwaW4lMjBvZmZpY2V8ZW58MHx8fHwxNzgxOTcwMzA5fDA&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
  const [projects, setProjects] = useState(null);
  const [roll, setRoll] = useState(null);
  const [open, setOpen] = useState(false);
  const [methodologies, setMethodologies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", industry: "", trueup_frequency: "weekly", planType: "Waterfall", use_template: true, start_date: "", end_date: "", work_on_weekends: false, timezone: "UTC", duration_unit: "days" });
  const [saving, setSaving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const pre = localStorage.getItem("pmo_onboarding_prefill");
      if (pre) {
        try {
          const p = JSON.parse(pre);
          setForm((f) => ({ ...f, industry: p.industry || f.industry, planType: p.planType || f.planType }));
        } catch (e) { console.debug("prefill parse failed", e); }
        localStorage.removeItem("pmo_onboarding_prefill");
      }
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      setProjects([]);
    }
    api.get("/portfolio").then(({ data }) => setRoll(data.overall)).catch(() => {});
    api.get("/methodologies").then(({ data }) => setMethodologies(data)).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const isCustom = form.planType.startsWith("tpl:");
  const selectedTemplate = isCustom ? templates.find((t) => `tpl:${t.id}` === form.planType) : null;
  const selectedLabel = isCustom ? (selectedTemplate?.name || "template") : form.planType;
  const selectedPhases = isCustom ? (selectedTemplate?.phases || []) : (methodologies.find((m) => m.name === form.planType)?.phases || []);

  const resetForm = () => setForm({ name: "", description: "", industry: "", trueup_frequency: "weekly", planType: "Waterfall", use_template: true, start_date: "", end_date: "", work_on_weekends: false, timezone: "UTC", duration_unit: "days" });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const base = { name: form.name, description: form.description, industry: form.industry, trueup_frequency: form.trueup_frequency };
      const payload = isCustom
        ? { ...base, template_id: selectedTemplate.id }
        : { ...base, methodology: form.planType, use_template: form.use_template };
      const { data } = await api.post("/projects", payload);
      setOpen(false);
      resetForm();
      toast.success("Project created");
      nav(`/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p) => {
    try {
      await api.post(`/projects/${p.id}/archive`);
      toast.success(`"${p.name}" archived — find it under Library → Archived Projects`, {
        action: { label: "Undo", onClick: async () => {
          try { await api.post(`/projects/${p.id}/unarchive`); toast.success(`"${p.name}" restored`); load(); }
          catch { toast.error("Could not restore"); }
        } },
      });
      load();
    } catch { toast.error("Could not archive project"); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="overline text-muted-foreground mb-2">Workspace</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-2">Build best-practice structured plans, timelines, and meeting next-steps.</p>
        </div>
        <div className="flex items-center gap-6">
          <AnimatedLogo backdrop className="hidden md:flex max-w-[170px] shrink-0 mr-2" data-testid="dashboard-earth-logo" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-11 gap-2" data-testid="new-project-button"><Plus className="h-4 w-4" /> New project</Button>
            </DialogTrigger>
            <DialogContent data-testid="new-project-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create a project</DialogTitle>
              <DialogDescription>Name your project, pick a plan type, and generate a structured plan next.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label htmlFor="pname">Project name</Label>
                <Input id="pname" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pind">Industry</Label>
                <Input id="pind" placeholder="e.g. Construction, SaaS, Healthcare" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="project-industry-input" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pstart">Start date</Label>
                  <Input id="pstart" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-date-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="pend">Target end date</Label>
                  <Input id="pend" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="project-end-date-input" className="mt-1.5" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Time zone</Label>
                  <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="project-timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration unit</Label>
                  <Select value={form.duration_unit} onValueChange={(v) => setForm({ ...form, duration_unit: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="project-duration-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DURATION_UNITS.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2.5 rounded-lg border border-border p-3 cursor-pointer hover:border-foreground/30 transition-all" data-testid="project-weekends-toggle">
                <Checkbox checked={!!form.work_on_weekends} onCheckedChange={(v) => setForm({ ...form, work_on_weekends: !!v })} data-testid="project-weekends-checkbox" />
                <div>
                  <p className="text-sm font-semibold">Count weekends as working days</p>
                  <p className="text-[11px] text-muted-foreground">Off = Sat/Sun skipped when scheduling & measuring duration.</p>
                </div>
              </label>
              <div>
                <Label>Plan type</Label>
                <Select value={form.planType} onValueChange={(v) => setForm({ ...form, planType: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="project-methodology-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Frameworks</SelectLabel>
                      {methodologies.map((m) => <SelectItem key={m.name} value={m.name} data-testid={`methodology-option-${m.name}`}>{m.name}</SelectItem>)}
                    </SelectGroup>
                    {templates.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Your templates</SelectLabel>
                        {templates.map((t) => <SelectItem key={t.id} value={`tpl:${t.id}`} data-testid={`template-option-${t.id}`}>{t.name} · {t.task_count} tasks</SelectItem>)}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
                {selectedPhases.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="methodology-phase-preview">
                    {selectedPhases.map((p, i) => (
                      <span key={p} className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary text-foreground">{p}</span>
                        {i < selectedPhases.length - 1 && <span className="text-muted-foreground text-[10px]">→</span>}
                      </span>
                    ))}
                  </div>
                )}
                {isCustom ? (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="custom-template-note">Your saved template's {selectedTemplate?.task_count || ""} tasks, milestones &amp; dependencies will be added to the new project.</p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mt-1.5">Uses an industry-standard framework. The AI plan generator follows these phases.</p>
                    <label className="flex items-center gap-2.5 mt-3 rounded-lg border border-border p-3 cursor-pointer hover:border-foreground/30 transition-all" data-testid="project-use-template-toggle">
                      <Checkbox checked={!!form.use_template} onCheckedChange={(v) => setForm({ ...form, use_template: !!v })} data-testid="project-use-template-checkbox" />
                      <div>
                        <p className="text-sm font-semibold">Start with a ready-made {selectedLabel} plan</p>
                        <p className="text-[11px] text-muted-foreground">Pre-fills standard tasks &amp; milestones you can edit. Uncheck to start empty.</p>
                      </div>
                    </label>
                  </>
                )}
              </div>
              <div>
                <Label htmlFor="pdesc">Scope / goal</Label>
                <Textarea id="pdesc" rows={3} placeholder="Describe what this project should achieve" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Progress true-up cadence</Label>
                <Select value={form.trueup_frequency} onValueChange={(v) => setForm({ ...form, trueup_frequency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="project-trueup-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRUEUP_FREQUENCIES.filter((f) => f.key !== "custom").map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">How often your team confirms % complete. Change anytime in project settings.</p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="project-create-submit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {roll && projects && projects.length > 0 && (
        <button onClick={() => nav("/portfolio")} className="w-full text-left border border-border rounded-lg p-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-3 hover:border-foreground/30 transition-colors" data-testid="dashboard-portfolio-strip">
          <span className="text-sm font-semibold flex items-center gap-2"><LayoutDashboard className="h-4 w-4 text-primary" /> Portfolio</span>
          <RollStat label="Avg progress" value={`${roll.avg_percent}%`} />
          <RollStat label="Off-track" value={roll.off_track_projects} tint={roll.off_track_projects ? "#EF4444" : undefined} />
          <RollStat label="Open risks" value={roll.open_risks} tint={roll.open_risks ? "#F59E0B" : undefined} />
          <RollStat label="Late actions" value={roll.late_actions} tint={roll.late_actions ? "#EF4444" : undefined} />
          <RollStat label="True-ups due" value={roll.trueup_due_projects} tint={roll.trueup_due_projects ? "#F59E0B" : undefined} />
          <span className="ml-auto text-xs text-primary font-semibold inline-flex items-center gap-1">View portfolio <ArrowUpRight className="h-3.5 w-3.5" /></span>
        </button>
      )}

      {projects === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : projects.length === 0 ? (
        <div className="border border-border rounded-lg overflow-hidden grid md:grid-cols-2" data-testid="empty-state">
          <div className="p-10 flex flex-col justify-center">
            <h3 className="font-display text-2xl font-bold tracking-tight">No projects yet</h3>
            <p className="text-muted-foreground mt-2 mb-6">Create your first project and let the AI draft a best-practice plan tailored to your industry.</p>
            <Button className="gap-2 w-fit" onClick={() => setOpen(true)} data-testid="empty-create-button"><Plus className="h-4 w-4" /> Create project</Button>
          </div>
          <img src={EMPTY_IMG} alt="team collaborating" className="h-full w-full object-cover min-h-[260px]" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
          {projects.map((p, idx) => {
            const pct = p.task_count ? Math.round((p.done_count / p.task_count) * 100) : 0;
            const fc = FOLDER_COLORS[idx % FOLDER_COLORS.length];
            return (
              <button
                key={p.id}
                onClick={() => nav(`/projects/${p.id}`)}
                className="text-left border border-border rounded-lg p-6 hover:-translate-y-1 hover:shadow-lg hover:border-foreground/30 transition-all duration-200 bg-card group"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="h-10 w-10 rounded-md flex items-center justify-center shadow-sm transition-transform group-hover:scale-110"
                    style={{ background: fc, boxShadow: `0 4px 12px -2px ${fc}66` }}
                  >
                    <FolderKanban className="h-5 w-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="flex items-center gap-1">
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight truncate">{p.name}</h3>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  {p.owner_name && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground/75" title="Project lead" data-testid={`project-lead-${p.id}`}>
                      <UserCircle2 className="h-3.5 w-3.5 text-primary" /> {p.owner_name}
                    </span>
                  )}
                  {p.industry && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Briefcase className="h-3 w-3" /> {p.industry}
                    </span>
                  )}
                </span>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2 min-h-[40px]">{p.description || "No scope described yet."}</p>
                <div className="mt-5">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>{p.done_count}/{p.task_count} tasks</span>
                    <span>{pct}%</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                  <div className="mt-3 flex justify-end">
                    <span role="button" tabIndex={0} title="Archive project" onClick={(e) => { e.stopPropagation(); setConfirmArchive(p); }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium opacity-40 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-amber-600 p-1 rounded"
                      data-testid={`archive-project-${p.id}`}>
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmArchive} onOpenChange={(v) => { if (!v) setConfirmArchive(null); }}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-amber-600" /> Archive "{confirmArchive?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This project will be <b>hidden from your dashboard, cards and week-at-a-glance</b>. Nothing is deleted — you can restore it anytime from Library → Archived Projects. Heads up: edits made while a project is archived are easy to lose track of.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-confirm-cancel">Keep it active</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={() => { const p = confirmArchive; setConfirmArchive(null); archive(p); }} data-testid="archive-confirm-button">
              Archive project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function RollStat({ label, value, tint }) {
  return (
    <span className="flex flex-col" data-testid={`roll-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-display text-lg font-bold" style={tint ? { color: tint } : {}}>{value}</span>
    </span>
  );
}
